import React, { useState, useEffect } from "react";
import { getAuthHeader } from "../../api/authToken.js";
import { useNavigate } from "react-router-dom";
import "../../styles/App.css";

// «Інші гуртожитки»: студенти за гуртожитками.
//  - «Усі гуртожитки» (типово) -- усі студенти; зверху окремий розділ
//    «Не вказано» зі студентами без гуртожитку/кімнати, далі решта;
//  - «Не вказано» -- лише студенти без кімнати (?dormitory_id=none);
//  - конкретний гуртожиток -- його мешканці (?dormitory_id=N).
// Порожні ПІБ, група, стать, курс, кімната показуються червоним «не вказано».

const ALL = "";
const NONE = "none";

export const MISSING_TEXT = "не вказано";

const GENDER_TEXT = { male: "чоловіча", female: "жіноча" };

const Missing = () => <span className="missing-value">{MISSING_TEXT}</span>;

const isBlank = (value) => value === null || value === undefined || String(value).trim() === "";

// Студент без гуртожитку/кімнати -- у розділ «Не вказано».
const isUnassigned = (s) => isBlank(s.room_id) || isBlank(s.dormitory_id);

const text = (value) => (isBlank(value) ? "" : String(value));

// Порівняння для сортування: порожні значення завжди в кінці, числа -- як
// числа, решта -- за українським алфавітом з урахуванням чисел у рядку ("101/2").
function compareValues(a, b) {
  const aBlank = isBlank(a);
  const bBlank = isBlank(b);
  if (aBlank || bBlank) {
    if (aBlank === bBlank) return 0;
    return aBlank ? 1 : -1;
  }
  if (typeof a === "number" && typeof b === "number") return a - b;
  return String(a).localeCompare(String(b), "uk", { numeric: true, sensitivity: "base" });
}

const COLUMNS = [
  { field: "pib", title: "ПІБ" },
  { field: "group", title: "Група" },
  { field: "dormitory_name", title: "Гуртожиток", allOnly: true },
  { field: "room_number", title: "Кімната" },
  { field: "gender", title: "Стать" },
  { field: "course", title: "Курс" },
];

const StudentsByDormitory = () => {
  const [dormitories, setDormitories] = useState([]);
  const [selectedDormitory, setSelectedDormitory] = useState(ALL);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [searchPib, setSearchPib] = useState("");
  const [searchGroup, setSearchGroup] = useState("");
  const [sortField, setSortField] = useState(null);
  const [sortOrder, setSortOrder] = useState("asc");
  const API = process.env.REACT_APP_API_BASE;

  const navigate = useNavigate();

  useEffect(() => {
    fetch(`${API}/api/dormitories`, {
      headers: { Authorization: getAuthHeader() },
    })
      .then((response) => response.json())
      .then((data) => setDormitories(Array.isArray(data) ? data : []))
      .catch((err) => console.error("Помилка:", err));
  }, [API]);

  useEffect(() => {
    // Попередній список не лишається на екрані під час завантаження нового,
    // а відповідь на застарілий вибір ігнорується.
    let cancelled = false;
    setStudents([]);
    setError("");
    setLoading(true);
    const query = selectedDormitory === ALL ? "" : `?dormitory_id=${encodeURIComponent(selectedDormitory)}`;
    fetch(`${API}/api/students-by-dormitory${query}`, {
      headers: { Authorization: getAuthHeader() },
    })
      .then((response) => {
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        return response.json();
      })
      .then((data) => {
        if (!cancelled) setStudents(Array.isArray(data) ? data : []);
      })
      .catch((err) => {
        console.error("Помилка:", err);
        if (!cancelled) setError("Не вдалося завантажити список студентів.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [API, selectedDormitory]);

  const handleRowClick = (studentId) => {
    navigate(`/student/${studentId}`);
  };

  const handleSort = (field) => {
    const newSortOrder = sortField === field && sortOrder === "asc" ? "desc" : "asc";
    setSortField(field);
    setSortOrder(newSortOrder);
  };

  const pibNeedle = searchPib.trim().toLowerCase();
  const groupNeedle = searchGroup.trim().toLowerCase();
  const filteredStudents = students.filter(
    (student) =>
      text(student.pib).toLowerCase().includes(pibNeedle) &&
      text(student.group).toLowerCase().includes(groupNeedle)
  );

  const sortedStudents = sortField
    ? [...filteredStudents].sort((a, b) => {
        const cmp = compareValues(a[sortField], b[sortField]);
        // Порожні -- в кінці за будь-якого напряму.
        if (isBlank(a[sortField]) || isBlank(b[sortField])) return cmp;
        return sortOrder === "asc" ? cmp : -cmp;
      })
    : filteredStudents;

  const allMode = selectedDormitory === ALL;
  const noneMode = selectedDormitory === NONE;
  const unassigned = allMode ? sortedStudents.filter(isUnassigned) : [];
  const assigned = allMode ? sortedStudents.filter((s) => !isUnassigned(s)) : sortedStudents;
  const columns = COLUMNS.filter((c) => !c.allOnly || allMode);

  const sortMark = (field) => {
    if (sortField !== field) return "↑↓";
    return sortOrder === "asc" ? "↑" : "↓";
  };
  const ariaSort = (field) => {
    if (sortField !== field) return undefined;
    return sortOrder === "asc" ? "ascending" : "descending";
  };

  const cell = (student, field) => {
    const value = student[field];
    if (isBlank(value)) return <Missing />;
    if (field === "gender") return GENDER_TEXT[value] || <Missing />;
    return value;
  };

  const renderTable = (rows, { highlight = false, testId } = {}) => (
    <table className="table" data-testid={testId}>
      <thead>
        <tr>
          {columns.map((c) => (
            <th
              key={c.field}
              className="table-header"
              aria-sort={ariaSort(c.field)}
              onClick={() => handleSort(c.field)}
              style={{ cursor: "pointer" }}
            >
              {c.title} {sortMark(c.field)}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((student) => (
          <tr
            key={student.id}
            className={`clickable-row${highlight ? " row-loses" : ""}`}
            tabIndex={0}
            onClick={() => handleRowClick(student.id)}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleRowClick(student.id);
            }}
          >
            {columns.map((c) => (
              <td key={c.field} className="table-content">
                {cell(student, c.field)}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );

  let emptyText = "Немає студентів для вибраного гуртожитку.";
  if (students.length) emptyText = "Немає студентів, що відповідають пошуку.";
  else if (noneMode) emptyText = "Немає студентів без гуртожитку чи кімнати.";
  else if (allMode) emptyText = "Немає студентів.";

  return (
    <div className="page-container">
      <div className="header-content">
        <h3 className="header-content-text">Студенти за гуртожитками</h3>

        <select
          className="header-content-select-1"
          id="dormitory-select"
          aria-label="Гуртожиток"
          value={selectedDormitory}
          onChange={(e) => setSelectedDormitory(e.target.value)}
          style={noneMode ? { color: "var(--full-busy)" } : undefined}
        >
          <option value={ALL} style={{ color: "initial" }}>
            Усі гуртожитки
          </option>
          <option value={NONE} style={{ color: "var(--full-busy)" }}>
            Не вказано
          </option>
          {dormitories.map((dormitory) => (
            <option key={dormitory.id} value={dormitory.id} style={{ color: "initial" }}>
              {dormitory.name}
            </option>
          ))}
        </select>

        <input
          type="text"
          className="header-content-input"
          placeholder="Пошук за ПІБ"
          aria-label="Пошук за ПІБ"
          value={searchPib}
          onChange={(e) => setSearchPib(e.target.value)}
        />
        <input
          type="text"
          className="header-content-input"
          placeholder="Пошук за групою"
          aria-label="Пошук за групою"
          value={searchGroup}
          onChange={(e) => setSearchGroup(e.target.value)}
        />
      </div>

      {loading && <p className="no-data">Завантаження...</p>}
      {error && (
        <p className="no-data" role="alert" style={{ color: "var(--full-busy)" }}>
          {error}
        </p>
      )}

      {!loading && !error && sortedStudents.length === 0 && <p className="no-data">{emptyText}</p>}

      {!loading && !error && sortedStudents.length > 0 && (
        <>
          {allMode && unassigned.length > 0 && (
            <section data-testid="unassigned-section" aria-labelledby="unassigned-heading">
              <h3 id="unassigned-heading" className="text-header section-missing">
                Не вказано ({unassigned.length})
              </h3>
              <p className="text-header">Студенти без гуртожитку чи кімнати.</p>
              {renderTable(unassigned, { highlight: true, testId: "unassigned-table" })}
            </section>
          )}

          {noneMode && (
            <h3 className="text-header section-missing">Не вказано ({sortedStudents.length})</h3>
          )}

          {assigned.length > 0 && (
            <section data-testid="students-section">
              {allMode && unassigned.length > 0 && (
                <h3 className="text-header">У гуртожитках ({assigned.length})</h3>
              )}
              {renderTable(assigned, { highlight: noneMode, testId: "students-table" })}
            </section>
          )}
        </>
      )}
    </div>
  );
};

export default StudentsByDormitory;
